import React, { useCallback, useMemo, useState } from 'react';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import DownloadFileFormComponent from 'system/components/batch-management/file-managment/download-anpr/list';
import { BatchDownloadAnprFileCrumbs } from '../../constants';
import { IconButton, Typography, useEventCallback } from '@mui/material';
import { useFormik } from 'formik';
import Table from 'shell/components/shared/table';
import { noop } from 'shell/utils/noop';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { __PRACTICE_API_URL__ } from 'shell/constants/environment';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { BatchFileAndDirectory } from 'shell/models/batch-file-managment';
import { Download } from '@mui/icons-material';
import { downloadBlob } from 'front-office/utils/download-blob';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { DateTime } from 'luxon';
import { BatchDownloadAnprFileFilters, BatchDownloadAnprFileManagment, PageInfo } from './types';
import { headCells, validationBatchDownloadAnprFile } from './constants';
import { initialValuesBatchDownloadAnpr } from '..';
import { BatchAnprFile } from 'shell/models/batch-anpr-file';
import { HttpResponseList } from 'shell/models/http-response';
import { Operation } from 'shell/view-models/operation';

const GestioneBatchDownloadAnprFile = (): React.ReactElement => {
  const [pageInfo, setPageInfo] = useState<PageInfo>({
    totalCount: 0,
    pageSize: initialValuesBatchDownloadAnpr.rowsPerPage,
    page: initialValuesBatchDownloadAnpr.page,
  });

  const [filesAndDirectories, getFilesAndDirectories] = useHttpFetch<
    HttpResponseList<BatchAnprFile>,
    Array<BatchDownloadAnprFileManagment>
  >(`${__PRACTICE_API_URL__}/pratiche/gestione-file-nfs/getANPR`, {
    onBootstrap: true,
    options: {
      errorCatching: true,
      method: 'GET',
      loading: true,
      params: {},
      paramsSerializer: () => {
        setPageInfo({
          totalCount: 0,
          pageSize: initialValuesBatchDownloadAnpr.rowsPerPage,
          page: initialValuesBatchDownloadAnpr.page,
        });

        const params = `filters=name:${downloadAnprFileForm.values.title}:${
          Operation.LIKE
        }&filters=timestamp:${
          downloadAnprFileForm.values.endDate?.toFormat('yyyy-MM-dd') || 'null'
        }:BEFORE&filters=timestamp:${
          downloadAnprFileForm.values.startDate?.toFormat('yyyy-MM-dd') || 'null'
        }:AFTER&pageOffset=${initialValuesBatchDownloadAnpr.page}&pageSize=${
          initialValuesBatchDownloadAnpr.rowsPerPage
        }`;
        return params;
      },
    },

    transformer: (response) => {
      const data = response?.data || [];
      if (data.payload.pageInfo) setPageInfo(data.payload.pageInfo);
      return data?.payload.list.map((item) => {
        const itemResult: BatchDownloadAnprFileManagment = {
          title: item?.name || '',
          updateDate: item?.dataAggiornamento
            ? DateTime.fromISO(item?.dataAggiornamento).toFormat('dd/MM/yyyy HH:mm:ss')
            : '',
        };
        return itemResult;
      });
    },
  });

  const getDownloadFile = useHttpClient<Blob>(
    `${__PRACTICE_API_URL__}/pratiche/gestione-file-nfs/downloadANPR`,
    {
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
      },
    }
  );

  const onSubmit = useEventCallback(() => {
    getFilesAndDirectories() as ResponsePromise<
      Array<BatchFileAndDirectory>,
      Array<BatchDownloadAnprFileManagment>
    >;
  });

  const downloadAnprFileForm = useFormik<BatchDownloadAnprFileFilters>({
    initialValues: initialValuesBatchDownloadAnpr,
    validationSchema: validationBatchDownloadAnprFile,
    onSubmit,
  });

  const handleChangePage = useCallback(
    async (newPage) => {
      try {
        setPageInfo({
          ...pageInfo,
          page: newPage,
        });
        downloadAnprFileForm?.setFieldValue('page', newPage);
        getFilesAndDirectories({
          paramsSerializer: () => {
            const params = `filters=name:${downloadAnprFileForm.values.title}:${
              Operation.LIKE
            }&filters=timestamp:${
              downloadAnprFileForm.values.endDate?.toFormat('yyyy-MM-dd') || 'null'
            }:BEFORE&filters=timestamp:${
              downloadAnprFileForm.values.startDate?.toFormat('yyyy-MM-dd') || 'null'
            }:AFTER&pageOffset=${newPage}&pageSize=${pageInfo.pageSize}`;
            return params;
          },
        });
      } catch (error) {}
    },
    [downloadAnprFileForm.setFieldValue, downloadAnprFileForm?.values, getFilesAndDirectories]
  );

  const handleChangeRowsPerPage = useCallback(
    async (rowsPerPage) => {
      try {
        setPageInfo({
          ...pageInfo,
          pageSize: rowsPerPage,
        });
        const newDataset = {
          ...downloadAnprFileForm.values,
          rowsPerPage: rowsPerPage || 0,
          page: 0,
        };
        downloadAnprFileForm.setValues(newDataset);
        getFilesAndDirectories({
          paramsSerializer: () => {
            const params = `filters=name:${downloadAnprFileForm.values.title}:${
              Operation.LIKE
            }&filters=timestamp:${
              downloadAnprFileForm.values.endDate?.toFormat('yyyy-MM-dd') || 'null'
            }:BEFORE&filters=timestamp:${
              downloadAnprFileForm.values.startDate?.toFormat('yyyy-MM-dd') || 'null'
            }:AFTER&pageOffset=${pageInfo.page}&pageSize=${rowsPerPage}`;
            return params;
          },
        });
      } catch (error) {}
    },
    [downloadAnprFileForm.setFieldValue, downloadAnprFileForm?.values, getFilesAndDirectories]
  );

  const onDownload = useEventCallback((item: BatchDownloadAnprFileManagment) => {
    (
      getDownloadFile({
        data: {
          pathFunzione: '/logs',
          fileName: item?.title,
        },
        responseType: 'blob',
      }) as ResponsePromise<Blob>
    ).then((response) => {
      if (response?.data) {
        downloadBlob(response?.data, item?.title);
      }
    });
  });

  const body: Array<BatchDownloadAnprFileManagment> = useMemo(() => {
    return (
      (filesAndDirectories?.data as Array<BatchDownloadAnprFileManagment>)?.map((item) => ({
        ...(item || {}),
        action: (
          <>
            <IconButton onClick={() => onDownload(item)}>
              <Download />
            </IconButton>
          </>
        ),
      })) || []
    );
  }, [filesAndDirectories?.data, onDownload]);

  const count: number = useMemo(() => {
    return pageInfo.totalCount;
  }, [body, pageInfo]);

  return (
    <>
      <Breadcrumbs items={BatchDownloadAnprFileCrumbs} />
      <div className="pmi-my-2">
        <Typography variant="h5">Download ANPR</Typography>
      </div>
      <div className="pmi-my-3">
        <DownloadFileFormComponent downloadFileForm={downloadAnprFileForm} />
      </div>
      {filesAndDirectories?.data ? (
        (filesAndDirectories?.data as Array<BatchDownloadAnprFileManagment>)?.length > 0 ? (
          <Table
            onSort={noop}
            head={headCells}
            orders={downloadAnprFileForm.values.orders}
            page={downloadAnprFileForm.values.page}
            rowsPerPage={downloadAnprFileForm.values.rowsPerPage}
            body={body}
            noSort
            hideButtons
            count={count}
            onPageChange={handleChangePage}
            isServer
            onRowsPerPageChange={handleChangeRowsPerPage}
            ariaLabel="Lista Files"
          />
        ) : (
          <Typography color="secondary.main">
            Nessun elemento trovato con la ricerca effettuata.
          </Typography>
        )
      ) : undefined}
    </>
  );
};

GestioneBatchDownloadAnprFile.displayName = 'Gestione-batch download file component';
export default GestioneBatchDownloadAnprFile;
